import React, { useState, useEffect, useRef, Component } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ReactReader } from 'react-reader';
import { Helmet } from 'react-helmet-async';
import '../styles.css';
import { logInteraction } from '../utils/tracker.js';
import { t } from '../i18n/translationStrings.js';

const S3_BUCKET_URL = process.env.REACT_APP_S3_BUCKET_URL;

// Monkey-patch console.warn to suppress EPUB.js 'No Element Provided' spam
const originalWarn = console.warn;
if (process.env.NODE_ENV === 'development') {
  console.warn = function (...args) {
    if (args[0]?.includes?.('No Element Provided')) return;
    originalWarn(...args);
  };
}

// Add Console Error Suppression Patch
const originalError = console.error;
console.error = function (...args) {
  const msg = args[0];
  if (
    typeof msg === 'string' &&
    (msg.includes('No Element Provided') ||
      msg.includes("Cannot read properties of undefined (reading 'appendChild')"))
  ) {
    return;
  }
  originalError(...args);
};

// ErrorBoundary component to catch ReactReader render errors
class ErrorBoundary extends Component {
  state = { hasError: false, errorMessage: null };

  static getDerivedStateFromError(error) {
    return { hasError: true, errorMessage: error.message };
  }

  componentDidCatch(error, errorInfo) {
    console.error('📚 ReactReader crashed:', error.message, errorInfo?.componentStack);
  }

  render() {
    if (this.state.hasError) {
      return (
        <p style={{ textAlign: 'center', color: 'var(--text-color)' }}>
          Failed to render: {this.state.errorMessage || 'Unknown error'}
        </p>
      );
    }
    return this.props.children;
  }
}

const normalizeName = (name) =>
  name
    .toLowerCase()
    .replace(/[.\-]/g, '')  // remove dots and dashes
    .replace(/\s+/g, ' ')   // normalize spacing
    .trim();

const BookPage = () => {
  const { titleSlug } = useParams();
  const [book, setBook] = useState(null);
  const [loading, setLoading] = useState(true);
  const [viewType, setViewType] = useState(null);
  const [epubLocation, setEpubLocation] = useState(undefined);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [relatedBooks, setRelatedBooks] = useState([]);
  const [genreBooks, setGenreBooks] = useState([]);
  const [moreByAuthor, setMoreByAuthor] = useState([]);
  const [signedUrl, setSignedUrl] = useState(null);
  const [error, setError] = useState(null);
  const readerContainerRef = useRef(null);

  const createSlug = (title) =>
    title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
  const truncateText = (text, maxLength) => {
    if (!text) return '';
    return text.length > maxLength ? text.slice(0, maxLength) + '...' : text;
  };

// helper near top (after S3_BUCKET_URL):
const joinS3 = (base, path) => {
  if (!path) return '';
  return path.startsWith('http')
    ? path
    : `${base}${path.startsWith('/') ? '' : '/'}${path}`;
};

// compute once and reuse
const resolvedCover =
  book?.coverImage
    ? joinS3(S3_BUCKET_URL, book.coverImage)
    : '/images/PlaceholderJPG.webp';

const ogImage = resolvedCover || 'https://www.hermesreads.com/images/default-og-image.jpg';

  useEffect(() => {
    const fetchBookData = async () => {
      try {
        const response = await fetch(`${S3_BUCKET_URL}/HermesTest/metadata.json`);
        if (!response.ok) {
          throw new Error('Failed to fetch book data');
        }
        const books = await response.json();

        books.forEach((book) => {
          if (!book.titleSlug) {
            book.titleSlug = createSlug(book.title);
          }
        });

        const selectedBook =
          books.find((b) => b.titleSlug === titleSlug) ||
          books.find((b) => b.id === titleSlug);

        if (selectedBook && (!book || selectedBook.id !== book.id)) {
          setBook(selectedBook);
          logInteraction('page_view', {
            page: 'BookPage',
            bookTitle: selectedBook.title || 'Unknown',
            bookId: selectedBook.id || 'Unknown',
          });

          const moreByAuthor = books
            .filter(
              (b) =>
                b.id !== selectedBook.id &&
                normalizeName(b.author) === normalizeName(selectedBook.author)
            )
            .slice(0, 3);

          setMoreByAuthor(moreByAuthor);

          const genreMatches = books.filter(
            (b) =>
              b.id !== selectedBook.id &&
              selectedBook.categories.some((cat) => b.categories.includes(cat)) &&
              !moreByAuthor.some((rb) => rb.id === b.id)
          );

          const genreShuffled = genreMatches.sort(() => 0.5 - Math.random()).slice(0, 3);
          setRelatedBooks(genreShuffled);

          setLoading(false);
        } else if (!selectedBook) {
          console.error('❌ No matching book found.');
          setLoading(false);
        }
      } catch (error) {
        console.error('Error fetching book data:', error);
        setError('Failed to fetch book data.');
        setLoading(false);
      }
    };

    fetchBookData();
  }, [titleSlug]);

  useEffect(() => {
    if (!book?.id) return;

    if (viewType !== null || signedUrl !== null || epubLocation !== undefined) {
      setViewType(null);
      setSignedUrl(null);
      setEpubLocation(undefined);
      setError(null);
    }
  }, [book?.id]);

  useEffect(() => {
    if (!book?.filePath || signedUrl || loading) return;

    const fetchSignedUrl = async () => {
      let attempt = 0;
      while (attempt < 3) {
        const controller = new AbortController();
        const timeout = setTimeout(() => controller.abort(), 10000);
        try {
          const correctedFilePath = book.filePath.startsWith('http')
            ? book.filePath.replace('https://hermeshermeshermes.s3.amazonaws.com/', '')
            : `HermesTest/${book.id}.epub`;

          console.log('📚 Book file path used:', correctedFilePath);
          console.log('✅ Fetching signed URL from:', process.env.REACT_APP_API_URL);

          const apiUrl = process.env.REACT_APP_API_URL || window.location.origin;
          const response = await fetch(
            `${apiUrl}/api/signed-url?filePath=${encodeURIComponent(correctedFilePath)}`,
            { signal: controller.signal }
          );
          clearTimeout(timeout);
          const rawText = await response.text();
          const data = JSON.parse(rawText);

          console.log('✅ Received signed URL:', data.signedUrl || '[no URL returned]');

          if (data.signedUrl) {
            setSignedUrl(data.signedUrl);
            return;
          }
        } catch (error) {
          console.warn(`⚠️ Failed to fetch Signed URL (Attempt ${attempt + 1}/3):`, error);
        }
        attempt++;
      }
      setError('Failed to load book. Please try again later.');
    };

    fetchSignedUrl();
  }, [book, signedUrl, loading]);

  useEffect(() => {
    if (book) {
      const structuredData = {
  '@context': 'https://schema.org',
  '@type': 'Book',
  name: book.title,
  author: { '@type': 'Person', name: book.author },
  description: book.description || 'No description available.',
  inLanguage: book.language || 'en',
  datePublished: book.publicationDate || 'Unknown',
  image: ogImage, // ✅ use the resolved url
  url: window.location.href,
  genre: (book.categories || []).join(', '), // ✅ safe join
  offers: {
    '@type': 'Offer',
    price: book.price || '0',
    priceCurrency: 'USD',
    availability: 'https://schema.org/InStock',
  },
};

      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.text = JSON.stringify(structuredData);
      document.head.appendChild(script);

      return () => {
        document.head.removeChild(script);
      };
    }
  }, [book]);

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
    };
  }, []);

  const toggleFullscreen = async () => {
    if (!isFullscreen) {
      try {
        await readerContainerRef.current.requestFullscreen();
      } catch (err) {
        console.error('Error entering fullscreen:', err);
      }
    } else {
      try {
        await document.exitFullscreen();
      } catch (err) {
        console.error('Error exiting fullscreen:', err);
      }
    }
  };

  const handleEpubPageChange = (location) => {
    console.log('📖 Page changed to:', location);
    setEpubLocation(location);
    logInteraction('epub_page_turn', { bookId: book.id, title: book.title, location });
  };

  if (loading) {
    return <p style={{ textAlign: 'center', color: 'var(--text-color)' }}>Loading book details...</p>;
  }

  if (!book) {
    return (
      <div className="error-message" style={{ textAlign: 'center', margin: '2rem' }}>
        <h2>Error</h2>
        <p>Book not found.</p>
        <Link to="/" style={{ color: 'var(--highlight-color)' }}>
          Return to Home
        </Link>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>{book ? `${book.title} by ${book.author} | HermesReads` : 'Book'}</title>
        <meta
          name="description"
          content={
            book
              ? `Read "${book.title}" by ${book.author}. Categories: ${book.categories.join(', ')}`
              : 'Discover and read your favorite books on HermesReads.'
          }
        />
        <meta
          name="keywords"
          content={
            book
              ? `${book.title}, ${book.author}, ${book.categories.join(', ')}, free ebooks`
              : 'books, ebooks, reading, literature, free books'
          }
        />
        <meta property="og:image" content={ogImage} />
        <link rel="canonical" href={`https://hermesreads.com/book/${titleSlug}`} />
      </Helmet>

      {!loading && (
        <main
          className={`book-container ${!loading ? 'loaded' : ''}`}
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            textAlign: 'center',
            padding: '20px',
            minHeight: '100vh',
            backgroundColor: 'var(--background-color)',
          }}
        >
          <div className="book-info" style={{ maxWidth: '600px', padding: '20px' }}>
            <h1
              id="book-title"
              style={{
                fontSize: '2rem',
                fontWeight: 'bold',
                marginBottom: '10px',
                color: 'var(--highlight-color)',
              }}
            >
              {truncateText(book.title, 89)}
            </h1>

            {book.description && (
              <p
                style={{
                  fontSize: '1.1rem',
                  color: 'var(--text-color)',
                  marginBottom: '15px',
                  whiteSpace: 'pre-wrap',
                }}
              >
                {book.description}
              </p>
            )}

            <h3
              id="book-author"
              style={{
                fontSize: '1.5rem',
                color: 'var(--text-color)',
                marginBottom: '20px',
              }}
            >
              {t('by')}{' '}
              <Link
                to={`/author/${createSlug(book.author)}`}
                style={{
                  color: 'var(--link-color)',
                  textDecoration: 'underline',
                  cursor: 'pointer',
                }}
              >
                {book.author}
              </Link>
            </h3>
            {book.coverImage && (
              <img
                src={resolvedCover} // ✅ use the resolved URL
                alt={`Cover of ${book.title} by ${book.author}`}
                loading="lazy"
                style={{ width: '100%', borderRadius: '8px' }}
                onError={(e) => (e.currentTarget.src = '/images/PlaceholderJPG.webp')}
              />
            )}
          </div>

          {error && (
            <div style={{ color: 'red', textAlign: 'center', marginBottom: '20px' }}>{error}</div>
          )}

          <div
            id="reader-container"
            ref={readerContainerRef}
            className={`reader ${isFullscreen ? 'fullscreen' : ''}`}
            style={{
              width: '100%',
              height: isFullscreen ? '100vh' : '80vh',
              border: '1px solid var(--light-gray)',
              borderRadius: '10px',
              marginBottom: '20px',
              backgroundColor: 'var(--light-gray)',
            }}
          >
            {viewType === 'epub' && signedUrl && book && book.filePath?.endsWith('.epub') ? (
              <ErrorBoundary>
                <ReactReader
                  url={signedUrl}
                  location={epubLocation}
                  locationChanged={handleEpubPageChange}
                  title={truncateText(book.title, 37)}
                  getRendition={(rendition) => {
                    rendition.flow('scrolled-doc');

                    rendition.book.ready.then(() => {
                      console.log('📖 EPUB is ready to render.');
                      const toc = rendition.book.navigation.toc;
                      const spineItems = rendition.book.spine?.items || [];

                      const isProbablyContent = (href) =>
                        !(
                          href.includes('cover') ||
                          href.includes('blank') ||
                          href.includes('title') ||
                          href.includes('copyright') ||
                          href.includes('colophon')
                        );
                      const firstContent =
                        spineItems.find(
                          (item, idx) =>
                            idx > 0 &&
                            isProbablyContent(item.href) &&
                            item.mediaType === 'application/xhtml+xml'
                        ) || spineItems[1];

                      let useSpineFallback = false;

                      if (!toc || toc.length === 0) {
                        useSpineFallback = true;
                      } else if (toc.length === 1) {
                        const tocTarget = toc[0].href.split('#')[0];
                        const idx = spineItems.findIndex((item) => item.href === tocTarget);
                        if (idx === spineItems.length - 1 || !isProbablyContent(tocTarget)) {
                          useSpineFallback = true;
                        }
                      }

                      if (useSpineFallback && firstContent) {
                        rendition.display(firstContent.href).catch((err) => {
                          setError('Failed to display book content.');
                        });
                      } else if (toc && toc.length > 0) {
                        rendition.display(toc[0].href).catch((err) => {
                          if (firstContent) {
                            rendition.display(firstContent.href).catch(() =>
                              setError('Failed to display book content.')
                            );
                          } else {
                            setError('Book is missing readable content.');
                          }
                        });
                      } else {
                        setError('This book could not be displayed properly.');
                      }
                    });

                    rendition.hooks.content.register((contents) => {
                      if (!contents?.document?.body) {
                        console.warn('⚠️ Skipping hook: No valid body in section.');
                        return;
                      }

                      contents.document.querySelectorAll('a[href]').forEach((link) => {
                        link.addEventListener('click', (e) => {
                          e.preventDefault();
                          const rawHref = link.getAttribute('href') || '';
                          if (!rawHref) {
                            console.warn('⚠️ Empty href in link, skipping.');
                            setError('This link is invalid.');
                            return;
                          }

                          const canonicalHref = rendition.book.canonical(rawHref);
                          const spineTarget = canonicalHref.split('#')[0];

                          const spineItem = rendition.book.spine.get(spineTarget);
                          if (!spineItem) {
                            console.warn('⚠️ Link target not found in spine:', spineTarget);
                            setError('This link appears broken inside the book.');
                            return;
                          }

                          rendition.display(canonicalHref).catch((err) => {
                            console.warn('⚠️ Link display failed with fragment:', canonicalHref, err.message);
                            rendition.display(spineTarget).catch((err2) => {
                              console.warn('⚠️ Section display also failed:', spineTarget, err2.message);
                              setError('Failed to open link inside book.');
                            });
                          });
                        });
                      });
                    });

                    rendition.themes.default({
                      img: { 'max-width': '100%' },
                      body: {
                        padding: '1rem',
                        'box-sizing': 'border-box',
                        'max-width': '100%',
                        overflow: 'hidden',
                      },
                      html: {
                        'overflow-x': 'hidden',
                      },
                    });
                  }}
                  styles={{
                    readerArea: { backgroundColor: 'var(--background-color)', color: 'var(--text-color)' },
                  }}
                />
              </ErrorBoundary>
            ) : viewType === 'epub' ? (
              error ? (
                <p style={{ textAlign: 'center', color: 'var(--text-color)' }}>{error}</p>
              ) : (
                <p style={{ textAlign: 'center', color: 'var(--text-color)' }}>Preparing book reader...</p>
              )
            ) : null}
            {isFullscreen && (
              <button
                onClick={toggleFullscreen}
                style={{
                  position: 'fixed',
                  top: '10px',
                  right: '10px',
                  padding: '10px',
                  backgroundColor: '#dc3545',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '50%',
                  cursor: 'pointer',
                  zIndex: 1000,
                }}
              >
                X
              </button>
            )}
          </div>

          <div
            id="reader-controls"
            style={{
              marginBottom: '20px',
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              flexWrap: 'wrap',
              justifyContent: 'center',
              width: '100%',
              maxWidth: '600px',
            }}
          >
            {book.filePath && book.filePath.endsWith('.epub') && (
              <button
                disabled={!signedUrl}
                title={!signedUrl ? 'Preparing secure link…' : 'Read Book'}
                onClick={() => {
                  setEpubLocation(undefined);
                  setViewType('epub');
                }}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#3e6b3e',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '5px',
                  cursor: 'pointer',
                  transition: 'background-color 0.3s ease',
                  fontSize: '1rem',
                  fontWeight: '500',
                  flex: 1,
                  minWidth: '150px',
                }}
                onMouseOver={(e) => (e.currentTarget.style.backgroundColor = '#2d4d2d')}
                onMouseOut={(e) => (e.currentTarget.style.backgroundColor = '#3e6b3e')}
              >
                Read Book
              </button>
            )}

            {viewType === 'epub' && (
              <button
                onClick={toggleFullscreen}
                style={{
                  padding: '10px 20px',
                  backgroundColor: isFullscreen ? '#dc3545' : '#3e6b3e',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '5px',
                  cursor: 'pointer',
                  transition: 'background-color 0.3s ease',
                  fontSize: '1rem',
                  fontWeight: '500',
                  flex: 1,
                  minWidth: '150px',
                }}
                onMouseOver={(e) =>
                  (e.currentTarget.style.backgroundColor = isFullscreen ? '#c82333' : '#2d4d2d')
                }
                onMouseOut={(e) =>
                  (e.currentTarget.style.backgroundColor = isFullscreen ? '#dc3545' : '#3e6b3e')
                }
              >
                {isFullscreen ? 'Exit Fullscreen' : 'Fullscreen Mode'}
              </button>
            )}
          </div>

          {relatedBooks.length > 0 && (
            <div className="related-books" style={{ marginTop: '16px', padding: '12px' }}>
              <h3 style={{ fontSize: '1.5rem', marginBottom: '20px', color: 'var(--highlight-color)' }}>
                {t('relatedBooks')}
              </h3>
              <div className="book-grid">
                {relatedBooks.map((relatedBook) => (
                  <Link
                    to={`/book/${relatedBook.titleSlug || createSlug(relatedBook.title)}`}
                    key={relatedBook.id || relatedBook.title}
                    className="book-link"
                  >
                    <div className="related-book-card">
                      <h4>{truncateText(relatedBook.title, 46)}</h4>
                      <p>By {truncateText(relatedBook.author, 20)}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}

          {moreByAuthor.length > 0 && (
            <div className="more-by-author" style={{ marginTop: '16px', padding: '12px' }}>
              <h3 style={{ fontSize: '1.5rem', marginBottom: '20px', color: 'var(--highlight-color)' }}>
                {t('moreBy')}{' '}
                <Link
                  to={`/author/${createSlug(book.author)}`}
                  style={{
                    color: 'var(--link-color)',
                    textDecoration: 'underline',
                    cursor: 'pointer',
                  }}
                >
                  {book.author}
                </Link>
              </h3>
              <div className="book-grid">
                {moreByAuthor.map((authorBook) => (
                  <Link
                    to={`/book/${authorBook.titleSlug || createSlug(authorBook.title)}`}
                    key={authorBook.id || authorBook.title}
                    className="book-link"
                  >
                    <div className="author-book-card">
                      <h4>{truncateText(authorBook.title, 46)}</h4>
                      <p>By {truncateText(authorBook.author, 20)}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </main>
      )}
    </>
  );
};

export default BookPage;